import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock3, ExternalLink, FileText, Mail, MessageCircle, Send, ShieldCheck } from 'lucide-react';
import { formatDateTime, todayIso } from '@/components/live';
import { callRpc, saveRow } from '@/api/resources';
import { uploadDocument } from '@/api/documents';
import { sendConfirmationNotice, sendRecordDocument } from '@/api/documentSend';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { toast } from '@/lib/toast';
import { htmlToPdf } from '@/lib/pdf';
import { buildUrdInvoiceHtml } from '@/lib/urdInvoice';
import { TRADE_PRODUCTS, optionLabel } from '@/lib/practiceLists';
import { OtpCapture } from '@/screens/parts/OtpCapture';
import { LinkedDocumentList } from '@/components/LinkedDocumentLink';

export interface UrdTrade {
  id: string; trade_code: string | null; transaction_date: string; seller_farmer_id: string | null; seller_display: string | null;
  buyer_display: string | null; buyer_gstin: string | null; buyer_address?: string | null; buyer_farmer_id?: string | null;
  product: string; product_detail: string | null; species_label: string | null;
  quantity: number; quantity_unit_label: string | null; rate: number | null; amount: number | null; total_amount: number;
  invoice_no: string | null; invoice_date: string | null; invoice_document_id: string | null; plot_code: string | null;
  harvest_code: string | null; claims_display: string | null; transit_permit_no: string | null; vehicle_no: string | null;
  seller_party_id?: string | null; buyer_party_id?: string | null; buyer_party_name?: string | null;
  supplier_confirmed_at?: string | null; supplier_confirmed_via?: string | null;
  buyer_confirmed_at?: string | null; buyer_confirmed_via?: string | null;
  claim_details?: Array<{ claim_label?: string; certificate?: string | null }> | null;
  hsn_code?: string | null; invoice_sent_at?: string | null; invoice_sent_via?: string[] | null;
}

interface FarmerAddress {
  id: string; party_id: string | null; full_name: string; farmer_code: string | null; mobile: string | null; communication_address: string | null;
  pin_code?: string | null; village_name: string | null; block_name: string | null; district_name: string | null; state_name: string | null;
}

/**
 * The farmer's self-declared (URD) invoice for a saved sale (owner, 2026-09-27): each party
 * confirms it by OTP to their own registered contact, then it is generated as a PDF carrying how
 * it was validated - "This is a digitally generated record, validated via ..." - reviewed, and kept
 * as the sale's invoice. An invoice the farmer already has is uploaded on the Invoice field instead.
 */
export function UrdInvoice({ trade }: { trade: UrdTrade }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'supplier' | 'buyer' | null>(null);
  // The draft stored when generated, so it can be sent before anyone confirms (stage 1).
  const [draftId, setDraftId] = useState<string | null>(null);
  const [sendBy, setSendBy] = useState<Array<'whatsapp' | 'email'>>(['whatsapp', 'email']);
  const farmer = useRows<FarmerAddress>('farmers', { filters: { id: trade.seller_farmer_id ?? undefined } }, Boolean(trade.seller_farmer_id));
  // A buyer who is a member farmer is addressed from their record; any other buyer, from the sale.
  const buyerFarmer = useRows<FarmerAddress>('farmers', { filters: { id: trade.buyer_farmer_id ?? undefined } }, Boolean(trade.buyer_farmer_id));
  const invoiceNo = trade.invoice_no || `URD/${trade.trade_code ?? trade.id.slice(0, 8)}`;
  const supplierParty = trade.seller_party_id ?? farmer.data?.[0]?.party_id ?? null;
  const buyerParty = trade.buyer_party_id ?? buyerFarmer.data?.[0]?.party_id ?? null;
  const bothConfirmed = Boolean(trade.supplier_confirmed_at && trade.buyer_confirmed_at);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const addressOf = (row?: FarmerAddress) => (row ? (row.communication_address
    ? `${row.communication_address}${row.pin_code ? ` - ${row.pin_code}` : ''}`
    : [row.village_name, row.block_name, row.district_name, row.state_name].filter(Boolean).join(', ')) : null);

  async function generate() {
    setBusy('generate');
    try {
      const f = farmer.data?.[0];
      const html = buildUrdInvoiceHtml({
        invoiceNo,
        // dd-mm-yyyy, as an invoice is dated; the screen's weekday form does not belong on one.
        invoiceDate: (trade.invoice_date ?? trade.transaction_date).split('-').reverse().join('-'),
        organisation: profile?.organization?.name ?? 'Vanam',
        supplier: { name: f?.full_name ?? trade.seller_display ?? '', code: f?.farmer_code, address: addressOf(f), phone: f?.mobile },
        buyer: { name: trade.buyer_party_name ?? trade.buyer_display ?? '', gstin: trade.buyer_gstin,
                 address: trade.buyer_address || addressOf(buyerFarmer.data?.[0]) },
        goods: {
          description: [optionLabel(TRADE_PRODUCTS, trade.product), trade.product_detail].filter(Boolean).join(', '),
          species: trade.species_label, quantity: trade.quantity, unit: trade.quantity_unit_label ?? '',
          rate: trade.rate, amount: trade.amount ?? trade.total_amount, hsn: trade.hsn_code,
        },
        claimLines: (trade.claim_details ?? []).map((line) => ({ claim: line.claim_label ?? '', certificate: line.certificate ?? null })),
        plot: trade.plot_code, harvest: trade.harvest_code, claims: trade.claims_display,
        transitPermitNo: trade.transit_permit_no, vehicleNo: trade.vehicle_no, tradeCode: trade.trade_code,
        confirmations: {
          supplier: trade.supplier_confirmed_at ? { via: trade.supplier_confirmed_via ?? 'OTP', at: trade.supplier_confirmed_at } : null,
          buyer: trade.buyer_confirmed_at ? { via: trade.buyer_confirmed_via ?? 'OTP', at: trade.buyer_confirmed_at } : null,
        },
      });
      const pdf = await htmlToPdf(html, `${invoiceNo.replace(/[\\/:*?"<>|]+/g, '-')}${bothConfirmed ? '' : '-draft'}.pdf`);
      setFile(pdf);
      setPreview(URL.createObjectURL(pdf));
      // A draft is kept on the sale at once, so it can be sent before anyone confirms.
      if (!bothConfirmed && profile?.organization) {
        const stored = await uploadDocument({ file: pdf, bucket: 'generated-documents', organizationId: profile.organization.id,
                                              ownerId: trade.seller_farmer_id ?? trade.id, captureMethod: 'generated', issuedDate: todayIso() });
        await callRpc('fn_link_document', { p_document_id: stored.id, p_entity_table: 'ops.trade_transactions', p_entity_id: trade.id, p_link_type: 'generated' });
        setDraftId(stored.id);
        await queryClient.invalidateQueries({ queryKey: ['record_documents'] });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The invoice could not be made.');
    } finally {
      setBusy(null);
    }
  }

  async function keep() {
    if (!file || !profile?.organization) return;
    setBusy('keep');
    try {
      const stored = await uploadDocument({ file, bucket: 'generated-documents', organizationId: profile.organization.id,
                                            ownerId: trade.seller_farmer_id ?? trade.id, captureMethod: 'generated', issuedDate: todayIso() });
      await callRpc('fn_link_document', { p_document_id: stored.id, p_entity_table: 'ops.trade_transactions', p_entity_id: trade.id, p_link_type: 'primary' });
      await saveRow('trade_transactions', trade.id, {
        invoice_document_id: stored.id, invoice_no: invoiceNo, invoice_date: trade.invoice_date ?? trade.transaction_date,
      });
      // The Invoice files list reads the links: it shows the final copy at once.
      await Promise.all(['trade_transactions', 'record_documents'].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
      toast.success(`${invoiceNo} kept as this sale's invoice.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The invoice could not be kept.');
    } finally {
      setBusy(null);
    }
  }

  const side = (who: 'supplier' | 'buyer', name: string | null, party: string | null, at?: string | null, via?: string | null) => (
    <li key={who}>
      <span className="child-record-text">
        <strong>{at ? <CheckCircle2 size={14} /> : <Clock3 size={14} />} {who === 'supplier' ? 'Supplier (farmer)' : 'Buyer'}: {name ?? '--'}</strong>
        <small className="muted">
          {at ? `Confirmed ${formatDateTime(at)} via ${via ?? 'OTP'}`
            : party ? 'Not confirmed yet.' : who === 'buyer' ? 'No registered contact: choose the Registered buyer on the sale (Edit, Buyer tab).' : 'The farmer has no registered contact.'}
        </small>
      </span>
      {!at && party && <button onClick={() => setConfirming(who)} type="button"><ShieldCheck size={14} /> Get OTP</button>}
    </li>
  );

  return (
    <section className="child-records">
      <div className="section-heading"><h3><FileText size={16} /> Self-declared invoice (URD)</h3></div>
      <p className="muted child-records-intro">
        For a farmer not registered under GST: a bill of supply built from this sale. The supplier and the buyer each confirm it with a
        one-time code sent to their own registered contact; the PDF then says so, with the channels and the time.
        {trade.invoice_document_id ? ' This sale already has an invoice on file; keeping a new one replaces it.' : ''}
      </p>
      <div className="consent-steps">
        <h3><FileText size={16} /> 1. Send draft</h3>
        <p className="muted">Generate the draft for both parties to read, and send it by WhatsApp or email; an SMS (no link) says it was sent.</p>
        <ul className="toggle-list">
          {(['whatsapp', 'email'] as const).map((channel) => {
            const on = sendBy.includes(channel);
            return (
              <li key={channel}>
                <span>{channel === 'email' ? <Mail size={14} /> : <MessageCircle size={14} />} {channel === 'email' ? 'Email' : 'WhatsApp'}</span>
                <button aria-pressed={on} className="onoff-toggle" type="button"
                        onClick={() => setSendBy(on ? sendBy.filter((c) => c !== channel) : [...sendBy, channel])}>{on ? 'On' : 'Off'}</button>
              </li>
            );
          })}
        </ul>
        <div className="inline-form-actions">
          <button disabled={Boolean(busy) || bothConfirmed || farmer.isLoading || buyerFarmer.isLoading} onClick={() => void generate()} type="button">
            <FileText size={14} /> {busy === 'generate' ? 'Making the PDF...' : 'Generate draft'}
          </button>
          <button disabled={!draftId || Boolean(busy)} onClick={() => { if (draftId) { setBusy('send'); void sendRecordDocument({ tradeId: trade.id, documentId: draftId, channels: sendBy, stage: 'draft' }).finally(() => setBusy(null)); } }} type="button">
            <Send size={14} /> {busy === 'send' ? 'Sending...' : 'Send draft'}
          </button>
          {preview && <button onClick={() => window.open(preview, '_blank', 'noopener')} type="button"><ExternalLink size={14} /> Open / print</button>}
        </div>

        <h3><ShieldCheck size={16} /> 2. Send OTP for confirmation</h3>
        <p className="muted">Each party confirms with a code to their own registered contact; <strong>3.</strong> they are then told at once that it is recorded.</p>
        <ul className="child-record-list">
          {side('supplier', trade.seller_display, supplierParty, trade.supplier_confirmed_at, trade.supplier_confirmed_via)}
          {side('buyer', trade.buyer_party_name ?? trade.buyer_display, buyerParty, trade.buyer_confirmed_at, trade.buyer_confirmed_via)}
        </ul>

        <h3><Send size={16} /> 4. Send final consented copies</h3>
        <p className="muted">Once both have confirmed: the final invoice, carrying how and when each confirmed, kept on the sale and sent to both.
          {trade.invoice_sent_at ? ` Last sent ${formatDateTime(trade.invoice_sent_at)} by ${(trade.invoice_sent_via ?? []).join(', ')}.` : ''}</p>
        <div className="inline-form-actions">
          <button className="primary" disabled={!bothConfirmed || Boolean(busy)} onClick={() => void generate()} type="button">
            <FileText size={14} /> {busy === 'generate' ? 'Making the PDF...' : 'Generate final invoice'}
          </button>
          <button disabled={!file || !bothConfirmed || Boolean(busy)} onClick={() => void keep()} type="button">
            {busy === 'keep' ? 'Keeping...' : 'Keep as the sale\'s invoice'}
          </button>
          <button className="primary" disabled={!bothConfirmed || !trade.invoice_document_id || Boolean(busy)}
                  onClick={() => { if (trade.invoice_document_id) { setBusy('final'); void sendRecordDocument({ tradeId: trade.id, documentId: trade.invoice_document_id, channels: sendBy, stage: 'final' }).finally(() => setBusy(null)); } }}
                  title={trade.invoice_document_id ? undefined : 'Keep the final invoice first.'} type="button">
            <Send size={14} /> {busy === 'final' ? 'Sending...' : 'Send final consented copies'}
          </button>
        </div>
      </div>
      {preview && <iframe className="consent-preview" src={preview} title="Invoice PDF" />}
      <h3 className="section-title">Invoice files</h3>
      <LinkedDocumentList entityId={trade.id} entityTable="ops.trade_transactions"
                          labels={{ generated: 'Draft sent for confirmation', primary: 'Final invoice (confirmed by both)' }} />
      {confirming && (
        <OtpCapture
          intro={`The ${confirming} confirms invoice ${invoiceNo} (${trade.quantity} ${trade.quantity_unit_label ?? ''}, ₹ ${(trade.amount ?? trade.total_amount).toLocaleString('en-IN')}). `
            + 'The code goes to their own registered contact and names the invoice and the amount. Read it back only if the details are right.'}
          linkedId={trade.id} linkedTable="ops.trade_transactions"
          onClose={() => setConfirming(null)}
          onVerified={async (otpEventId) => {
            await callRpc('fn_confirm_sale_invoice', { p_trade_id: trade.id, p_side: confirming, p_otp_event_id: otpEventId });
            await queryClient.invalidateQueries({ queryKey: ['trade_transactions'] });
            toast.success(`${confirming === 'supplier' ? 'Supplier' : 'Buyer'} confirmed the invoice.`);
            // Stage 3: the party is told at once that their confirmation is recorded.
            void sendConfirmationNotice({ tradeId: trade.id, side: confirming });
            setConfirming(null);
            setFile(null);
            setPreview(null);
          }}
          partyId={(confirming === 'supplier' ? supplierParty : buyerParty) ?? ''}
          subtitle={invoiceNo}
          title={`Confirm the invoice - ${confirming}`}
        />
      )}
    </section>
  );
}
